<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { useValidationHelpers } from "@/helpers/validation.helper";
import { VraagType } from "@/repositories/activiteit.repository";
import type { VraagAntwoord } from "@/stores/sportactiviteit-bestel.store";

const props = defineProps<{
    vraagAntwoord: VraagAntwoord;
    /** Unique name prefix to avoid duplicate id's when the same component is rendered multiple times */
    prefix: string;
}>();

const rules = {
    vraagAntwoord: {
        Antwoord: { required: helpers.withMessage("Dit veld is verplicht", required) },
    },
};

const v$ = useVuelidate(rules, props);
const { getFieldErrors } = useValidationHelpers(v$);

const inputId = `${props.prefix}-vraag-${props.vraagAntwoord.Id}`;
</script>

<template>
    <div class="form-group mb-2" :class="{ 'has-error': v$.vraagAntwoord.Antwoord.$error }">
        <label :for="inputId">
            {{ vraagAntwoord.Vraag }} <span class="text-danger">*</span>
        </label>

        <!-- Open vraag -->
        <input
            v-if="vraagAntwoord.VraagType === VraagType.OpenVraag"
            type="text"
            class="form-control"
            :id="inputId"
            :name="inputId"
            v-model="vraagAntwoord.Antwoord"
            maxlength="255"
            placeholder="Antwoord"
            @blur="v$.vraagAntwoord.Antwoord.$touch()"
        />

        <!-- Gesloten vraag (Ja / Nee) -->
        <template v-else-if="vraagAntwoord.VraagType === VraagType.GeslotenVraag">
            <div class="custom-control custom-radio">
                <input
                    class="custom-control-input"
                    type="radio"
                    :id="`${inputId}-Ja`"
                    :name="inputId"
                    v-model="vraagAntwoord.Antwoord"
                    value="Ja"
                    @change="v$.vraagAntwoord.Antwoord.$touch()"
                />
                <label class="custom-control-label" :for="`${inputId}-Ja`">Ja</label>
            </div>
            <div class="custom-control custom-radio">
                <input
                    class="custom-control-input"
                    type="radio"
                    :id="`${inputId}-Nee`"
                    :name="inputId"
                    v-model="vraagAntwoord.Antwoord"
                    value="Nee"
                    @change="v$.vraagAntwoord.Antwoord.$touch()"
                />
                <label class="custom-control-label" :for="`${inputId}-Nee`">Nee</label>
            </div>
        </template>

        <div v-for="err in getFieldErrors('vraagAntwoord.Antwoord')" :key="err" class="ic-error" role="alert">
            <small class="text-danger">{{ err }}</small>
        </div>
    </div>
</template>
